import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRight, BookOpen, Sparkles, MessageSquare, BarChart3, Target, ExternalLink, X } from 'lucide-react';

// Birthday Banner Component (Mar 16-18, 2026)
const BirthdayBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');

  const promoEnd = new Date('2026-03-18T23:59:59Z');
  const promoStart = new Date('2026-03-16T00:00:00Z');

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      if (now < promoStart || now > promoEnd) {
        setIsVisible(false);
        return;
      }
      const diff = promoEnd - now;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      setTimeLeft(days > 0 ? `${days}d ${hours}h left` : `${hours}h left`);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('birthday_banner_dismissed') === 'true') {
      setIsVisible(false);
    }
  }, []);

  if (!isVisible) return null;

  return (
    <div className="mt-6 bg-amber-100 border border-amber-300 rounded-xl p-4" data-testid="birthday-banner">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🎂</span>
          <div>
            <p className="font-semibold text-amber-900">Birthday Special!</p>
            <p className="text-sm text-amber-800">Book 1 ebook is <strong>FREE on Amazon</strong> until March 18th</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://www.amazon.co.uk/dp/B0GKPPK9M8"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-700 transition-colors"
            data-testid="birthday-banner-cta"
          >
            Get Free Book
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          {timeLeft && (
            <span className="text-xs text-amber-700 font-medium">{timeLeft}</span>
          )}
          <button
            onClick={() => {
              sessionStorage.setItem('birthday_banner_dismissed', 'true');
              document.querySelector('[data-testid="birthday-banner"]')?.remove();
            }}
            className="p-1 hover:bg-amber-200 rounded transition-colors text-amber-700"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const SeriesHero = ({ series }) => {
  const book2 = series.books[1];
  const book3 = series.books[2];
  const coverImg = book2?.coverImage || series.books[0]?.coverImage;

  return (
    <section className="hero-section relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Background — warm cream */}
      <div className="absolute inset-0 bg-gradient-to-br from-stone-100 via-stone-50 to-amber-50">
        <div
          className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl animate-pulse opacity-10"
          style={{ backgroundColor: series.accentColor }}
        />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl animate-pulse opacity-10"
          style={{ backgroundColor: series.accentColor, animationDelay: '1s' }}
        />
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="h-full w-full" style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }} />
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <Badge
                className="border hover:opacity-80"
                style={{
                  backgroundColor: `${series.accentColor}15`,
                  color: series.accentColor,
                  borderColor: `${series.accentColor}40`
                }}
              >
                <Sparkles className="w-3 h-3 mr-1" />
                30 Day Skills
              </Badge>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="text-slate-900">Your </span>
                <span style={{ color: series.accentColor }}>Leadership</span>
                <br />
                <span className="text-slate-900">Operating System</span>
              </h1>

              <p className="text-lg lg:text-xl text-slate-600 max-w-xl leading-relaxed">
                A structured development system for IT managers. Books, AI companion, and measurable progress — working together.
              </p>
            </div>

            {/* System cycle — compact */}
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-200">
                <BookOpen className="w-3.5 h-3.5" style={{ color: series.accentColor }} /> Read
              </span>
              <ArrowRight className="w-3 h-3 text-slate-300 hidden sm:block" />
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-200">
                <Target className="w-3.5 h-3.5" style={{ color: series.accentColor }} /> Apply
              </span>
              <ArrowRight className="w-3 h-3 text-slate-300 hidden sm:block" />
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-200">
                <MessageSquare className="w-3.5 h-3.5" style={{ color: series.accentColor }} /> Reflect
              </span>
              <ArrowRight className="w-3 h-3 text-slate-300 hidden sm:block" />
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-200">
                <BarChart3 className="w-3.5 h-3.5" style={{ color: series.accentColor }} /> Measure
              </span>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="text-white px-10 py-7 text-xl group transition-all duration-300 shadow-lg"
                style={{
                  backgroundColor: series.accentColor,
                  boxShadow: `0 10px 40px ${series.accentColor}40`
                }}
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-start-free-btn"
              >
                Start Free
                <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-slate-300 text-slate-600 hover:bg-slate-100 px-6 py-6 text-base"
                onClick={() => document.getElementById('try-companion')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Test it out live
              </Button>
            </div>

            {/* Birthday Banner */}
            <BirthdayBanner />

            {/* Quick stats */}
            <div className="flex gap-8 pt-2">
              <div className="text-center">
                <div className="text-3xl font-bold" style={{ color: series.accentColor }}>8</div>
                <div className="text-sm text-slate-500">Books</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold" style={{ color: series.accentColor }}>240</div>
                <div className="text-sm text-slate-500">Daily Lessons</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold" style={{ color: series.accentColor }}>1</div>
                <div className="text-sm text-slate-500">AI Companion</div>
              </div>
            </div>
          </div>

          {/* Right side — Book covers stacked with system label */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              <div
                className="absolute inset-0 blur-3xl rounded-full scale-90 opacity-20"
                style={{ backgroundColor: series.accentColor }}
              />

              <div className="relative">
                {/* Main cover */}
                <div className="transform hover:scale-105 transition-transform duration-500">
                  <div className="absolute inset-0 bg-black/20 blur-xl translate-y-4 translate-x-4 rounded-lg" />
                  {coverImg ? (
                    <img
                      src={coverImg}
                      alt="30 Day Skills Leadership OS"
                      className="relative w-64 lg:w-72 h-auto rounded-lg shadow-2xl"
                    />
                  ) : (
                    <div className="relative w-64 lg:w-72 h-96 bg-slate-800 rounded-lg shadow-2xl flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-slate-600" />
                    </div>
                  )}
                </div>

                {/* Book 1 peeking behind-left */}
                {series.books[0]?.coverImage && book2?.coverImage && (
                  <div className="absolute -left-8 top-6 -z-10 opacity-60 transform -rotate-6">
                    <img
                      src={series.books[0].coverImage}
                      alt="Book 1"
                      className="w-56 lg:w-64 h-auto rounded-lg shadow-lg"
                    />
                  </div>
                )}

                {/* Book 3 peeking behind-right */}
                {book3?.coverImage && (
                  <div className="absolute -right-8 top-6 -z-10 opacity-60 transform rotate-6">
                    <img
                      src={book3.coverImage}
                      alt="Book 3"
                      className="w-56 lg:w-64 h-auto rounded-lg shadow-lg"
                    />
                  </div>
                )}

                {/* System badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-lg px-4 py-2 shadow-xl border" style={{ borderColor: `${series.accentColor}30` }}>
                  <span className="text-xs text-slate-500">30 Day Skills</span>
                  <span className="font-bold ml-1" style={{ color: series.accentColor }}>Leadership OS</span>
                </div>

                <div className="absolute -top-4 -right-4 bg-white rounded-lg p-3 shadow-xl animate-bounce" style={{ animationDuration: '3s' }}>
                  <Sparkles className="w-6 h-6" style={{ color: series.accentColor }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeriesHero;
